// Pure arithmetic + parsing helpers for PartEntryPage's Pricing & Costing card.
//
// Every dollar the Part page shows must be reproducible from what is on the
// screen, so the math lives here (testable, no React) and the page only
// renders. Nothing in this module throws on missing / null / string input.
//
// Findings addressed (see audit_condensed.md): F06 stored-vs-live rollup,
// F07 assembly-owned coating / at-join steps, F08 calc-log table, F14/F15
// single SERVER rollup formula, F16 material provenance, F28 "Calculated
// Price" basis, F32 allocation utilisation, F33 help text.

import { num, approxEq, fmtMoney, fmtPct, fmtHours, asOf } from './priceMath';

/** parts.markup_percent is a PERCENT (10 = 10%); NULL means 10, 0 stays 0. */
export const DEFAULT_MARKUP_PERCENT = 10;

/** A cut-sheet / cut-list allocation below this share of the stock is flagged. */
export const LOW_UTILISATION_PCT = 25;

export const COST_FORMULA = 'labor + gas + consumables + material + coating';
export const PRICE_FORMULA = 'labor + gas + consumables + (material + coating) × (1 + markup%)';

/** NULL / '' / non-numeric -> 10; a deliberate 0 stays 0 (mirrors the server's `is not None`). */
export function normalizeMarkupPercent(v) {
  if (v === null || v === undefined || v === '') return DEFAULT_MARKUP_PERCENT;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : DEFAULT_MARKUP_PERCENT;
}

/** The six component fields as plain numbers (strings / nulls tolerated). */
export function rollupTerms(components) {
  const c = components || {};
  return {
    labor: num(c.cost_labor),
    gas: num(c.estimated_gas_cost),
    consumables: num(c.estimated_consumable_cost),
    material: num(c.material_cost_component),
    coating: num(c.coating_cost_per_unit),
    markup_percent: normalizeMarkupPercent(c.markup_percent),
  };
}

/**
 * THE rollup — the same formula the server's update_all_part_costs (and, after
 * Workstream B, PATCH /parts) uses. The page used to price coating at cost on
 * the price side (F15); it now marks coating up with material like the server.
 *
 *   cost  = labor + gas + consumables + material + coating
 *   price = labor + gas + consumables + (material + coating) × (1 + markup% / 100)
 */
export function computePartRollup(components) {
  const t = rollupTerms(components);
  const markup_multiplier = 1 + t.markup_percent / 100;
  const cost_labor_material = t.labor + t.gas + t.consumables + t.material + t.coating;
  const price_labor_material = t.labor + t.gas + t.consumables + (t.material + t.coating) * markup_multiplier;
  return { cost_labor_material, price_labor_material, markup_multiplier, terms: t };
}

/** Terms for priceMath.formulaString — the cost side. */
export function costFormulaTerms(rollup) {
  const t = rollup.terms;
  return [['labor', t.labor], ['gas', t.gas], ['consumables', t.consumables], ['material', t.material], ['coating', t.coating]];
}

/** Terms for priceMath.formulaString — the price side. */
export function priceFormulaTerms(rollup) {
  const t = rollup.terms;
  return [
    ['labor', t.labor], ['gas', t.gas], ['consumables', t.consumables],
    ['material + coating', t.material + t.coating, { mul: rollup.markup_multiplier }],
  ];
}

/** Stored-vs-live comparison for one figure. `stale` is to the half-cent. */
export function freshness(stored, live) {
  const s = num(stored, NaN); const l = num(live, NaN);
  if (!Number.isFinite(s) || !Number.isFinite(l)) return { stale: false, delta: null, comparable: false };
  return { stale: !approxEq(s, l), delta: l - s, comparable: true };
}

// ---------------------------------------------------------------------------
// Material price method helpers (F16 / F32)
// ---------------------------------------------------------------------------

/** Methods whose math the CWT / linear debug box actually describes. */
export function isMaterialDebugMethod(method) {
  return ['weight', 'bounding_area', 'solid_profile', 'cut_length'].includes(method);
}

/**
 * Classify parts.material_price_method into the same `kind` vocabulary the API's
 * material.source uses. Server contract (2026-09-02): 'manual' and 'manual_price'
 * BOTH mean "the typed manual_material_price; $0 / blank = customer supplied".
 * Pass `manualPrice` to apply that rule. Without it the legacy mapping is
 * returned ('manual' → customer_supplied, 'manual_price' → manual), which is
 * what a pre-provenance API actually does with the column.
 */
export function materialMethodKind(method, { manualPrice } = {}) {
  if (!method) return 'none';
  if (method === 'weight') return 'cwt_weight';
  if (method === 'bounding_area') return 'cwt_bounding';
  if (method === 'solid_profile') return 'cwt_solid';
  if (method === 'cut_length') return 'cut_length';
  if (method.startsWith('nested_cut_list_')) return 'cut_list';
  if (method.startsWith('nested_')) return 'cut_sheet';
  if (method === 'manual_price' || method === 'manual') {
    if (manualPrice === undefined) return method === 'manual_price' ? 'manual' : 'customer_supplied';
    return num(manualPrice) > 0 ? 'manual' : 'customer_supplied';
  }
  return 'none';
}

/**
 * Label + kind for the 'manual' radio.
 *   - material_live resolved THIS method (and the typed price is unchanged on the
 *     page) → follow source.kind: the typed price for 'manual', "Customer supplied /
 *     no cost" for 'customer_supplied';
 *   - material_live present but for another stored method (operator switched on
 *     this page) → the typed price on the page decides, same rule as the server;
 *   - material_live absent (older API) → the legacy label, unchanged.
 */
export function manualMethodLabel({ materialLive, describesThisMethod, manualPrice, legacyLabel }) {
  const ml = materialLive || {};
  if (ml.available && describesThisMethod) {
    if (ml.kind === 'manual') {
      const v = ml.value ?? ml.unitCostSourceValue ?? num(manualPrice);
      return { text: `Manual price (typed): ${fmtMoney(v)}`, kind: 'manual' };
    }
    if (ml.kind === 'customer_supplied') return { text: 'Customer supplied / no cost', kind: 'customer_supplied' };
  }
  if (ml.available) {
    const p = num(manualPrice);
    return p > 0
      ? { text: `Manual price (typed): ${fmtMoney(p)}`, kind: 'manual' }
      : { text: 'Customer supplied / no cost', kind: 'customer_supplied' };
  }
  return { text: legacyLabel, kind: null };
}

/** The cut_sheet_part_id / cut_list_part_id encoded in a nested_* method, else null. */
export function materialMethodRowId(method) {
  if (!method) return null;
  let m = method.match(/^nested_cut_list_(\d+)$/);
  if (m) return parseInt(m[1], 10);
  m = method.match(/^nested_(\d+)$/);
  if (m) return parseInt(m[1], 10);
  return null;
}

export function utilisationIsLow(pct, threshold = LOW_UTILISATION_PCT) {
  const n = num(pct, NaN);
  return Number.isFinite(n) && n < threshold;
}

/** "2.7% of stock" from a percent (0–100) or a fraction (0–1 when `fraction`). */
export function describeUtilisation(pct, { fraction = false } = {}) {
  const n = num(pct, NaN);
  if (!Number.isFinite(n)) return null;
  return `${fmtPct(fraction ? n : n / 100, 1)} of stock`;
}

/**
 * Normalise the API's `material_live` (Workstream B2) into what the radios and
 * the material row render. Tolerates the field being absent (older API).
 */
export function describeMaterialLive(materialLive) {
  if (!materialLive || typeof materialLive !== 'object') {
    return { available: false, value: null, kind: null, description: null, rowId: null, utilisationPct: null, low: false, unresolved: [] };
  }
  const src = materialLive.source || {};
  const util = src.utilisation_pct ?? src.utilization_pct ?? null;
  return {
    available: true,
    value: materialLive.value === null || materialLive.value === undefined ? null : num(materialLive.value),
    method: materialLive.method || null,
    kind: src.kind || null,
    description: src.description || null,
    rowId: src.row_id ?? null,
    utilisationPct: util === null ? null : num(util),
    low: utilisationIsLow(util),
    // _describe_material_source sets row_stale when the cut-sheet / cut-list row's
    // own stored value no longer matches the live sheet / stick maths.
    rowStale: !!src.row_stale,
    unitCostSourceValue: src.unit_cost_source_value ?? null,
    // Why the engine could not work the material out (a cut sheet row that no longer exists,
    // no vendor price…): value is then null — unknown, not $0 (jobq-api cost propagation step 1).
    unresolved: Array.isArray(materialLive.unresolved) ? materialLive.unresolved.filter(Boolean) : [],
  };
}

/**
 * Best-effort description of a /parts/{id}/cut_sheets or /cut_lists row.
 * Today those rows carry only material_cost; if a later API adds the sheet /
 * stick maths (sheet_cost, quantity, utilisation_pct …) they are shown.
 */
export function describeAllocationRow(row, kind) {
  if (!row) return { text: '', utilisationPct: null, low: false };
  const bits = [];
  const stockCost = row.sheet_cost ?? row.stick_cost ?? row.stock_cost ?? null;
  const qty = row.quantity ?? row.qty ?? null;
  if (stockCost !== null && stockCost !== undefined) {
    bits.push(`${kind === 'cut_list' ? 'stick' : 'sheet'} ${fmtMoney(stockCost)}${qty ? ` ÷ ${qty} pcs` : ''}`);
  }
  const util = row.utilisation_pct ?? row.utilization_pct ?? null;
  const utilText = describeUtilisation(util);
  if (utilText) bits.push(utilText);
  if (row.description && !bits.length) bits.push(String(row.description));
  return { text: bits.join(' · '), utilisationPct: util === null ? null : num(util), low: utilisationIsLow(util) };
}

// ---------------------------------------------------------------------------
// Calculation log parser (F08)
// ---------------------------------------------------------------------------

function stripParens(s) {
  const t = (s || '').trim();
  const m = t.match(/^\((.*)\)$/);
  return (m ? m[1] : t).trim();
}

/**
 * Pull hours / $/hr / rate-note / $ / trailing note out of the RHS of a log
 * line. Handles every shape the two engines emit today:
 *   "$0.3751"
 *   "$12.50/hr * 0.0300 hrs = $0.3750"          (calculate_cost gas)
 *   "0.0300 hrs * $12.50/hr = $0.38"            (dynamic gas)
 *   "0.0300 hrs * $0.49/hr (Nozzle+Lens) = $0.0147"
 *   "0.0362 hrs * $21.21/hr punch-weighted 90d (12 punches) = $0.77"   (post-B1 rate label)
 *   "0.0500 hrs * $27.00/hr default wage table via Fabrication (fab fallback) = $1.35"
 *   "$0.00 (Insufficient data for theoretical calc)"
 *   "0.0100 hrs"                                 (quote-engine "+ Shuttle overhead" — hours only)
 */
export function parseMathExpr(rest) {
  const out = { hours: null, rate: null, rateNote: null, cost: null, note: null };
  const text = (rest || '').trim();
  if (!text) return out;
  const eqIdx = text.lastIndexOf('=');
  const costPart = eqIdx >= 0 ? text.slice(eqIdx + 1) : text;
  const cm = costPart.match(/\$\s*(-?[\d.,]+)/);
  if (cm) out.cost = parseFloat(cm[1].replace(/,/g, ''));
  const nm = costPart.match(/\(([^)]*)\)\s*$/);
  if (nm) out.note = nm[1].trim();
  if (eqIdx < 0) {
    // Hours-only line (no "= $x"): keep the hours so the table can still print them.
    const hm0 = text.match(/(-?[\d.]+)\s*hrs?\b/i);
    if (hm0) out.hours = parseFloat(hm0[1]);
  }
  if (eqIdx >= 0) {
    let lhs = text.slice(0, eqIdx);
    const hm = lhs.match(/(-?[\d.]+)\s*hrs?\b/i);
    if (hm) { out.hours = parseFloat(hm[1]); lhs = lhs.replace(hm[0], ' '); }
    lhs = lhs.replace(/\*/g, ' ').trim();
    const rm = lhs.match(/\$\s*(-?[\d.,]+)\s*\/\s*hr\s*(.*)$/i);
    if (rm) {
      out.rate = parseFloat(rm[1].replace(/,/g, ''));
      const note = stripParens(rm[2]);
      out.rateNote = note || null;
    }
  }
  return out;
}

/** 'historical' | 'estimate' | null from the "(Historical)" / "(Initial Est.)" tag. */
export function classifyHoursSource(tag, engine) {
  const t = (tag || '').toLowerCase();
  if (t.includes('historical') || t.includes('history')) return 'historical';
  if (t.includes('initial') || t.includes('est')) return 'estimate';
  if (!t && engine === 'dynamic') return 'estimate';
  return null;
}

/** 'default' | 'punch_weighted_90d' | null from the text after "$xx/hr". */
export function classifyRateSource(note) {
  const t = (note || '').toLowerCase();
  if (!t) return null;
  if (t.includes('default')) return 'default';
  if (t.includes('punch') || t.includes('weighted')) return 'punch_weighted_90d';
  return null;
}

/** 'historical' | 'estimate' | 'mixed' | null over the step rows. */
export function summarizeHoursSource(rows) {
  const set = new Set((rows || []).filter(r => r.kind === 'step' && r.source).map(r => r.source));
  if (set.size === 0) return null;
  if (set.size === 1) return [...set][0];
  return 'mixed';
}

/**
 * Parse the plain-text log returned by GET /parts/{id}/calculate_cost (engine
 * 'history_aware') and POST /parts/calculate-dynamic-cost (engine 'dynamic')
 * into table rows: {kind, step, source, hours, rate, rateSource, rateNote, cost, note, raw}.
 * kind ∈ 'step' | 'gas' | 'consumables' | 'outsourced' | 'skipped' | 'shuttle' | 'other'.
 * Unparseable lines are kept as kind 'other' so nothing the engine said is lost.
 */
export function parseCalcLog(log) {
  const text = typeof log === 'string' ? log : (Array.isArray(log) ? log.join('\n') : '');
  const rows = []; const headers = []; const summary = { labor: null, gas: null };
  let engine = 'unknown';
  for (const line of text.split(/\r?\n/)) {
    const t = line.trim();
    if (!t) continue;
    if (/^Cost Calculation Log/i.test(t)) { engine = 'history_aware'; headers.push(t); continue; }
    if (/^Dynamic Cost Calculation Log/i.test(t)) { engine = 'dynamic'; headers.push(t); continue; }
    if (/^-{2,}/.test(t)) { headers.push(t.replace(/^-+\s*|\s*-+$/g, '')); continue; }
    let m = t.match(/^Labor:\s*\$([\d.,]+)\s*\|\s*Gas:\s*\$([\d.,]+)/i);
    if (m) { summary.labor = parseFloat(m[1].replace(/,/g, '')); summary.gas = parseFloat(m[2].replace(/,/g, '')); continue; }

    m = t.match(/^-?\s*Gas (?:Cost|Estimate)(?:\s*\(([^)]*)\))?:\s*(.*)$/i);
    if (m) {
      const tag = (m[1] || '').trim();
      const x = parseMathExpr(m[2]);
      let source = null;
      if (/historical/i.test(tag)) source = 'historical';
      else if (/beam/i.test(tag)) source = 'theoretical_beam_on';
      else if (/total/i.test(tag)) source = 'theoretical_total_time';
      else if (x.note) source = 'none';
      rows.push({ kind: 'gas', step: 'Gas', source, sourceTag: tag || null, hours: x.hours, rate: x.rate,
        rateSource: null, rateNote: x.rateNote, cost: x.cost, note: x.note, raw: t });
      continue;
    }
    // "Consumables: …" (history engine) / "- Consumables (Beam On): …" (estimate engine, post-B1)
    m = t.match(/^-?\s*Consumables(?:\s*\(([^)]*)\))?:\s*(.*)$/i);
    if (m) {
      const tag = (m[1] || '').trim();
      const x = parseMathExpr(m[2]);
      rows.push({ kind: 'consumables', step: 'Consumables', source: x.hours != null ? 'at_cost' : (x.note ? 'none' : null),
        sourceTag: tag || null, hours: x.hours, rate: x.rate, rateSource: null, rateNote: x.rateNote, cost: x.cost, note: x.note, raw: t });
      continue;
    }
    // Wave 2 (decision 4): the first-run proportional table-swap share is its own line.
    //   "- Laser shuttle share (first-run proportional): 0.0100 hrs * $21.21/hr = $0.21"
    //   "- Laser shuttle share: included in historical hours"
    //   "  + Shuttle overhead: 0.0100 hrs"                       (quote engine, hours only)
    m = t.match(/^[-+]?\s*(laser\s+shuttle(?:\s+share)?|shuttle\s+(?:share|overhead))(?:\s*\(([^)]*)\))?:\s*(.*)$/i);
    if (m) {
      const tag = (m[2] || '').trim();
      const rhs = (m[3] || '').trim();
      const x = parseMathExpr(rhs);
      const tagged = classifyHoursSource(tag, engine);
      const source = tagged || (/histor/i.test(rhs) ? 'historical' : 'estimate');
      rows.push({ kind: 'shuttle', step: 'Laser shuttle share', source, sourceTag: tag || null, hours: x.hours, rate: x.rate,
        rateSource: classifyRateSource(x.rateNote) || classifyRateSource(x.note), rateNote: x.rateNote, cost: x.cost,
        note: x.note || (x.cost === null && x.hours === null && rhs ? rhs : null), raw: t });
      continue;
    }
    m = t.match(/^-\s*([^:]+?):\s*outsourced(?:,\s*\$([\d.,]+)\s*\/\s*unit)?\s*(.*)$/i);
    if (m) {
      rows.push({ kind: 'outsourced', step: m[1].trim(), source: 'vendor', sourceTag: null, hours: null, rate: null, rateSource: null,
        rateNote: null, cost: m[2] ? parseFloat(m[2].replace(/,/g, '')) : null,
        note: m[2] ? 'vendor $/unit (not internal labour)' : (m[3] || 'no internal labour').replace(/^[—-]\s*/, ''), raw: t });
      continue;
    }
    m = t.match(/^-\s*([^:]+?):\s*Rate \$0\.00\s*\/\s*hr\s*(?:\(([^)]*)\))?/i);
    if (m) {
      rows.push({ kind: 'skipped', step: m[1].trim(), source: null, sourceTag: null, hours: null, rate: 0, rateSource: null,
        rateNote: null, cost: 0, note: m[2] ? m[2].trim() : 'skipped', raw: t });
      continue;
    }
    m = t.match(/^-\s*([^:(]+?)\s*(?:\(([^)]*)\))?:\s*(.*)$/);
    if (m && /\$/.test(m[3])) {
      const x = parseMathExpr(m[3]);
      const tag = (m[2] || '').trim();
      rows.push({ kind: 'step', step: m[1].trim(), source: classifyHoursSource(tag, engine), sourceTag: tag || null,
        hours: x.hours, rate: x.rate, rateSource: classifyRateSource(x.rateNote) || classifyRateSource(x.note), rateNote: x.rateNote, cost: x.cost, note: x.note, raw: t });
      continue;
    }
    rows.push({ kind: 'other', step: null, source: null, sourceTag: null, hours: null, rate: null, rateSource: null, rateNote: null, cost: null, note: null, raw: t });
  }
  return { engine, headers, rows, summary, hoursSource: summarizeHoursSource(rows) };
}

// ---------------------------------------------------------------------------
// Assembly membership (F07)
// ---------------------------------------------------------------------------

/**
 * Merge a row of GET /parts/{id}.assemblies with the matching GET /assemblies
 * record so role / join / ppq are known even when the part API predates B2.
 */
export function normalizeAssemblyRow(row, allAssemblies, partId) {
  const r = row || {};
  const full = (allAssemblies || []).find(a => a.assembly_id === r.assembly_id) || {};
  const pid = partId === null || partId === undefined ? null : Number(partId);
  let role = r.role || null;
  // Re-normalising an already-normalised row (GET /assemblies arriving after the
  // part on a deep link) must keep its provenance: a role inferred earlier stays "inferred".
  let roleSource = r.roleSource || (role ? 'api' : null);
  if (!role && full.primary_part_id !== undefined && pid !== null) {
    role = full.primary_part_id === pid ? 'primary' : 'component';
    roleSource = 'inferred';
  }
  const ppq = r.primary_part_quantity ?? full.primary_part_quantity ?? null;
  let qty = r.quantity_per_assembly ?? null;
  // A primary's qty per set IS the assembly's ppq; unknown stays null (rendered "—"), never a guessed 1.
  if (qty === null && role === 'primary') qty = ppq;
  return {
    ...r,
    assembly_id: r.assembly_id,
    assembly_name: r.assembly_name || full.assembly_name || `#${r.assembly_id}`,
    role,
    roleSource,
    join_workstation: r.join_workstation ?? full.join_workstation ?? null,
    primary_part_quantity: ppq,
    quantity_per_assembly: qty,
    ops_owned_steps: Array.isArray(r.ops_owned_steps) ? r.ops_owned_steps : [],
    assembly_prices_coating: !!r.assembly_prices_coating,
    // False when the part API predates B2 — "no ops-owned steps" is then unknown, not "none".
    // Preserved verbatim on a second pass (normalisation itself adds ops_owned_steps: []).
    has_provenance: typeof r.has_provenance === 'boolean'
      ? r.has_provenance
      : (Array.isArray(r.ops_owned_steps) || r.assembly_prices_coating !== undefined),
    active_status: r.active_status ?? full.active_status ?? null,
  };
}

/** "Primary of #126 526-3103 BRACKET AS, joins at Assembly" */
export function assemblyRoleSentence(asm) {
  if (!asm) return '';
  const name = `#${asm.assembly_id} ${asm.assembly_name || ''}`.trim();
  const join = asm.join_workstation ? `, joins at ${asm.join_workstation}` : '';
  if (asm.role === 'primary') {
    const ppq = asm.primary_part_quantity && asm.primary_part_quantity > 1 ? ` (×${asm.primary_part_quantity} per set)` : '';
    return `Primary of ${name}${ppq}${join}`;
  }
  if (asm.role === 'component') {
    const q = asm.quantity_per_assembly ? ` (×${asm.quantity_per_assembly} per set)` : '';
    return `Component of ${name}${q}${join}`;
  }
  return `In ${name}${join}`;
}

/** { 'Welding': [{assembly_id, assembly_name}], ... } — steps an assembly's ops line prices. */
export function opsOwnedStepsIndex(assemblies) {
  const idx = {};
  (assemblies || []).forEach(a => {
    (a.ops_owned_steps || []).forEach(step => {
      const key = String(step).trim();
      if (!key) return;
      if (!idx[key]) idx[key] = [];
      idx[key].push({ assembly_id: a.assembly_id, assembly_name: a.assembly_name });
    });
  });
  return idx;
}

/** Assemblies whose operations line carries a priced Coating row (the part's coating is NOT quoted on the part). */
export function coatingOwners(assemblies) {
  return (assemblies || []).filter(a => a.assembly_prices_coating);
}

/** Case-insensitive lookup of an ops-owned step in the index. */
export function opsOwnersForStep(index, step) {
  if (!index || !step) return [];
  const key = Object.keys(index).find(k => k.toLowerCase() === String(step).toLowerCase());
  return key ? index[key] : [];
}

/**
 * What the quote engine costs this part at INSIDE an assembly that owns
 * coating and/or at-join steps: the live total minus those lines. Only steps
 * that are both ops-owned and on the part's routing are subtracted; a step
 * with no parseable log row makes the figure approximate.
 */
export function asQuotedInsideAssembly({ liveTotal, coating, coatingOwned, logRows, opsOwnedIndex, processSteps }) {
  const excluded = [];
  let approximate = false;
  if (coatingOwned && num(coating) > 0) excluded.push({ label: 'coating', amount: num(coating) });
  const steps = (processSteps || []).map(s => String(s).trim()).filter(Boolean);
  steps.forEach(step => {
    if (opsOwnersForStep(opsOwnedIndex, step).length === 0) return;
    const row = (logRows || []).find(r => (r.kind === 'step' || r.kind === 'skipped') && r.step && r.step.toLowerCase() === step.toLowerCase());
    if (row && row.cost !== null && row.cost !== undefined) {
      excluded.push({ label: `${step} labor`, amount: num(row.cost) });
    } else {
      approximate = true;
      excluded.push({ label: `${step} labor`, amount: 0, unknown: true });
    }
  });
  const value = num(liveTotal) - excluded.reduce((s, e) => s + (e.unknown ? 0 : e.amount), 0);
  return { value, excluded, approximate, applies: excluded.length > 0 };
}

// ---------------------------------------------------------------------------
// Laser shuttle share (wave 2, decision 4)
// ---------------------------------------------------------------------------

/**
 * Normalise the shuttle provenance the two part-cost engines return post-wave-2:
 *   calculated_shuttle_cost — first-run proportional table-swap labour at the TRUE
 *                             rate, ALREADY INSIDE calculated_labor_cost;
 *   laser: {cutting_hours, shuttle_hours, hours_source, shuttle_included}.
 * Tolerates the keys being absent (older API) → available:false, never NaN.
 */
export function describeShuttle(resp) {
  const r = resp && typeof resp === 'object' ? resp : {};
  const laser = r.laser && typeof r.laser === 'object' ? r.laser : null;
  const hasCost = r.calculated_shuttle_cost !== undefined && r.calculated_shuttle_cost !== null;
  if (!hasCost && !laser) {
    return { available: false, cost: null, hours: null, cuttingHours: null, hoursSource: null, included: null, historyIncludes: false };
  }
  const pick = (v) => (v === undefined || v === null ? null : num(v));
  const hoursSource = laser && laser.hours_source ? String(laser.hours_source) : null;
  return {
    available: true,
    cost: hasCost ? num(r.calculated_shuttle_cost) : null,
    hours: laser ? pick(laser.shuttle_hours) : null,
    cuttingHours: laser ? pick(laser.cutting_hours) : null,
    hoursSource,
    included: laser && typeof laser.shuttle_included === 'boolean' ? laser.shuttle_included : null,
    // Historical Laser hours already carry the table swaps, so no separate share is charged.
    historyIncludes: hoursSource === 'historical',
  };
}

/**
 * The "Laser shuttle share" line under Cost - Labor: {text, kind (SourceTag), amount}.
 *   older API                → "n/a — API predates shuttle provenance"
 *   hours_source historical  → "first-run only — history includes it"
 *   estimate, share charged  → "$0.21 (0.0100 h) — first-run proportional table-swap, inside Cost - Labor"
 *   estimate, nothing charged→ "$0.00 — none charged …"
 *   no Laser step            → "no Laser step"
 */
export function shuttleSentence(sh) {
  if (!sh || !sh.available) return { text: 'n/a — API predates shuttle provenance', kind: 'default', amount: null };
  const amount = sh.cost === null ? (sh.hours ? null : 0) : sh.cost;
  if (sh.historyIncludes) {
    return { text: 'first-run only — history includes it', kind: 'historical', amount };
  }
  if (sh.hoursSource === 'none') return { text: 'no Laser step', kind: 'default', amount: amount ?? 0 };
  const charged = (amount !== null && amount > 0) || (sh.hours !== null && sh.hours > 0);
  if (charged) {
    const hrs = sh.hours !== null ? ` (${fmtHours(sh.hours)})` : '';
    return { text: `${fmtMoney(amount, { dash: true })}${hrs} — first-run proportional table-swap, inside Cost - Labor`, kind: 'estimate', amount };
  }
  return { text: `${fmtMoney(amount ?? 0)} — none charged (no Laser step, or the estimate already carries table-swap overhead)`, kind: 'default', amount: amount ?? 0 };
}

/**
 * Calc-log rows with a 'shuttle' row guaranteed when the engine reported a share
 * but its log carries no "Laser shuttle share" line (older log format). Inserted
 * right after the Laser step row when there is one, else appended. Never
 * duplicates a parsed shuttle row.
 */
export function withShuttleRow(rows, sh) {
  const list = Array.isArray(rows) ? rows : [];
  if (!sh || !sh.available) return list;
  if (list.some(r => r.kind === 'shuttle')) return list;
  const row = {
    kind: 'shuttle', step: 'Laser shuttle share', source: sh.historyIncludes ? 'historical' : 'estimate', sourceTag: null,
    hours: sh.hours, rate: null, rateSource: null, rateNote: null, cost: sh.cost,
    note: sh.historyIncludes ? 'first-run only — history includes it' : null, raw: '', synthesized: true,
  };
  const laserIdx = list.findIndex(r => r.kind === 'step' && String(r.step || '').toLowerCase() === 'laser');
  if (laserIdx < 0) return [...list, row];
  return [...list.slice(0, laserIdx + 1), row, ...list.slice(laserIdx + 1)];
}

// ---------------------------------------------------------------------------
// Outsourcing card as the single editor of outsourced $/piece (wave 2, decision 3)
// ---------------------------------------------------------------------------

/** The part's Coating routing row from GET /parts/{id}/processes (the card's data). */
export function findCoatingStep(steps) {
  return (Array.isArray(steps) ? steps : []).find(s => String(s?.process_name || '').toLowerCase() === 'coating') || null;
}

/** The preferred vendor_process_prices row for a step, else null. */
export function preferredPriceFor(step, prices) {
  if (!step) return null;
  return (Array.isArray(prices) ? prices : []).find(p => p && p.process_id === step.process_id && p.is_preferred) || null;
}

/** True when the row's $/piece IS the preferred book price (same vendor when the row names one; same $ to 4 dp). */
export function stepMatchesBook(step, book) {
  if (!step || !book) return false;
  if (step.vendor_id !== null && step.vendor_id !== undefined && book.vendor_id !== step.vendor_id) return false;
  if (step.outsourced_cost_per_unit === null || step.outsourced_cost_per_unit === undefined) return false;
  return approxEq(step.outsourced_cost_per_unit, book.price_per_piece, 0.00005);
}

function whenText(iso) {
  const s = asOf(iso);
  return s ? s.replace(/^as of /, '') : null;
}

/**
 * Sentence for the READ-ONLY "Coating $/unit (outsourced)" mirror on the Part page.
 * `partSaved` false = no card yet (unsaved part); `loaded` false = the card has not
 * reported its rows yet. `applied` is true only when the row carries the preferred
 * vendor's book price ("book price applied from <vendor> on <date>").
 */
export function describeCoatingMirror({ steps, prices, loaded, partSaved } = {}) {
  const base = 'set on the Outsourcing card → Coating row';
  const out = { text: base, kind: 'default', applied: false, step: null, book: null, vendorName: null, when: null };
  if (!partSaved) return { ...out, text: `${base} (after the part is created)` };
  if (!loaded) return out;
  const step = findCoatingStep(steps);
  if (!step) return { ...out, text: `${base} — no Coating step on this part's routing` };
  const book = preferredPriceFor(step, prices);
  const when = whenText(step.updated_at);
  const common = { ...out, step, book, vendorName: step.vendor_name || null, when };
  if (!step.is_outsourced) {
    return { ...common, text: `Coating row is not flagged outsourced on the card${when ? ` (row updated ${when})` : ''}` };
  }
  if (book && stepMatchesBook(step, book)) {
    return { ...common, kind: 'vendor', applied: true,
      text: `book price applied from ${book.vendor_name || 'the preferred vendor'}${when ? ` on ${when}` : ''}` };
  }
  const vendor = step.vendor_name ? `vendor ${step.vendor_name}` : 'no vendor';
  const diff = book
    ? `; preferred book price ${book.vendor_name || ''} ${fmtMoney(book.price_per_piece, { precise: true, dash: true })} differs — Apply it on the card`.replace('  ', ' ')
    : '';
  return { ...common, text: `${vendor} · typed on the card${when ? ` ${when}` : ''}${diff}` };
}

/**
 * The ONLY way coating_cost_per_unit enters the Part page's save payload.
 * parts.coating_cost_per_unit has one editor — the Outsourcing card (PATCH
 * /parts/{id}/processes/{ppid} writes the row AND the part in one transaction),
 * plus the price-book write-through (set-preferred / RFQ award) and the nightly.
 * Sending the page's copy back on every Save reverted those writes with a 200, so
 * the key is present ONLY when the card changed it this session — and then the
 * value is the one the server already holds.
 */
export function coatingPayloadFromCard(cardValue) {
  if (cardValue === null || cardValue === undefined || cardValue === '') return {};
  const n = num(cardValue, NaN);
  return Number.isFinite(n) ? { coating_cost_per_unit: n } : {};
}

/**
 * Notice text for the Outsourcing card after POST …/process_prices/{vpp}/set-preferred.
 * Post-wave-2 the endpoint writes the book price through and returns
 * `applied: [{part_id, process, old, new}]`; an empty list means the step is not
 * outsourced on this part (nothing written); an absent key means an older API.
 */
export function describePriceBookApply(resp, { stepName, vendorName, price } = {}) {
  const r = resp && typeof resp === 'object' ? resp : {};
  const who = vendorName ? `${vendorName}'s` : 'the preferred';
  const p = fmtMoney(price, { precise: true, dash: true });
  if (Array.isArray(r.applied)) {
    if (r.applied.length === 0) {
      return { ok: false, text: `Preferred vendor set (${vendorName || 'vendor'} ${p}) but the $/piece was NOT written through — ${r.why || 'the step is not outsourced on this part'}. Type it on the row if it should apply.` };
    }
    const bits = r.applied.map(a => `${a.process || stepName || 'step'} ${fmtMoney(a.old, { precise: true, dash: true })} → ${fmtMoney(a.new, { precise: true, dash: true })}`);
    const coating = r.applied.some(a => /coating/i.test(String(a.process || stepName || '')));
    return { ok: true, text: `Applied ${who} book price: ${bits.join('; ')} — written to this part's routing row${coating ? ' and parts.coating_cost_per_unit' : ''}.` };
  }
  return { ok: false, text: `Preferred vendor set (${vendorName || 'vendor'} ${p}). This API does not write the price through to the part — type the $/piece on the row.` };
}

